import { useState, useEffect } from "react";
import { contentService, supabase } from "../lib/supabase";

// Hook for fetching and managing individual content sections
export const useContent = (sectionKey, page = "home", defaultContent = "") => {
  const [content, setContent] = useState(defaultContent);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        setLoading(true);
        const data = await contentService.getContent(sectionKey, page);
        if (data && data.content) {
          setContent(data.content);
        }
      } catch (err) {
        setError(err);
        console.error(`Error fetching content for ${sectionKey}:`, err);
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, [sectionKey, page]);

  const updateContent = async (newContent) => {
    try {
      await contentService.upsertContent(sectionKey, newContent, page);
      setContent(newContent);
      return true;
    } catch (err) {
      setError(err);
      console.error(`Error updating content for ${sectionKey}:`, err);
      return false;
    }
  };

  return { content, loading, error, updateContent };
};

// Hook for managing services with real-time updates
export const useServices = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        setLoading(true);
        const data = await contentService.getServices();
        setServices(data);
      } catch (err) {
        setError(err);
        console.error("Error fetching services:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchServices();

    // Set up real-time subscription for services
    const subscription = supabase
      .channel('services-changes')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'services' },
        (payload) => {
          console.log('Services changed:', payload);
          // Refresh services when any change occurs
          fetchServices();
        }
      )
      .subscribe();

    // Cleanup subscription on unmount
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const refreshServices = async () => {
    try {
      const data = await contentService.getServices();
      setServices(data);
    } catch (err) {
      setError(err);
    }
  };

  return { services, loading, error, refreshServices };
};

// Hook for managing team members with real-time updates
export const useTeamMembers = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTeamMembers = async () => {
      try {
        setLoading(true);
        const data = await contentService.getTeamMembers();
        setTeamMembers(data);
      } catch (err) {
        setError(err);
        console.error("Error fetching team members:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchTeamMembers();

    // Set up real-time subscription for team members
    const subscription = supabase
      .channel('team-changes')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'team_members' },
        (payload) => {
          console.log('Team members changed:', payload);
          fetchTeamMembers();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const refreshTeamMembers = async () => {
    try {
      const data = await contentService.getTeamMembers();
      setTeamMembers(data);
    } catch (err) {
      setError(err);
    }
  };

  return { teamMembers, loading, error, refreshTeamMembers };
};

// Hook for managing FAQ items with real-time updates
export const useFAQItems = (category = null) => {
  const [faqItems, setFaqItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchFAQItems = async () => {
      try {
        setLoading(true);
        const data = await contentService.getFAQItems(category);
        setFaqItems(data);
      } catch (err) {
        setError(err);
        console.error("Error fetching FAQ items:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchFAQItems();

    // Set up real-time subscription for FAQ items
    const subscription = supabase
      .channel('faq-changes')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'faq_items' },
        (payload) => {
          console.log('FAQ items changed:', payload);
          fetchFAQItems();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [category]);

  const refreshFAQItems = async () => {
    try {
      const data = await contentService.getFAQItems(category);
      setFaqItems(data);
    } catch (err) {
      setError(err);
    }
  };

  return { faqItems, loading, error, refreshFAQItems };
};

// Hook for managing prices with real-time updates
export const usePrices = () => {
  const [prices, setPrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPrices = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from("prices")
          .select("*")
          .eq("is_active", true)
          .order("category", { ascending: true })
          .order("service_name", { ascending: true });

        if (error) throw error;
        setPrices(data || []);
      } catch (err) {
        setError(err);
        console.error("Error fetching prices:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchPrices();

    // Set up real-time subscription for prices
    const subscription = supabase
      .channel('prices-changes')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'prices' },
        (payload) => {
          console.log('Prices changed:', payload);
          fetchPrices();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const refreshPrices = async () => {
    try {
      const { data, error } = await supabase
        .from("prices")
        .select("*")
        .eq("is_active", true)
        .order("category", { ascending: true })
        .order("service_name", { ascending: true });

      if (error) throw error;
      setPrices(data || []);
    } catch (err) {
      setError(err);
    }
  };

  return { prices, loading, error, refreshPrices };
};

// Hook for managing locations with real-time updates
export const useLocations = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from("locations")
          .select("*")
          .eq("is_active", true)
          .order("order_index", { ascending: true });

        if (error) throw error;
        setLocations(data || []);
      } catch (err) {
        setError(err);
        console.error("Error fetching locations:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchLocations();

    // Set up real-time subscription for locations
    const subscription = supabase
      .channel('locations-changes')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'locations' },
        (payload) => {
          console.log('Locations changed:', payload);
          fetchLocations();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const refreshLocations = async () => {
    try {
      const { data, error } = await supabase
        .from("locations")
        .select("*")
        .eq("is_active", true)
        .order("order_index", { ascending: true });

      if (error) throw error;
      setLocations(data || []);
    } catch (err) {
      setError(err);
    }
  };

  return { locations, loading, error, refreshLocations };
};

// Hook for image uploading
export const useImageUpload = () => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const uploadImage = async (file) => {
    try {
      setUploading(true);
      setError(null);
      const imageUrl = await contentService.uploadImage(file);
      return imageUrl;
    } catch (err) {
      setError(err);
      console.error("Error uploading image:", err);
      throw err;
    } finally {
      setUploading(false);
    }
  };

  return { uploadImage, uploading, error };
};
